import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

import LoadingState from "@/components/ui/loading-state";
import { filesystemService } from "../../utils/filesystem";
import { observer } from "mobx-react-lite";
import { pyodideService } from "../../services/PyodideService";
import { rootStore } from "../../stores/RootStore";

const CreateNewProjectPage = observer(() => {
    const params = useParams({ from: "/org/$orgSlug/create-new-project" });
    const search = useSearch({ from: "/org/$orgSlug/create-new-project" });
    const navigate = useNavigate();
    const { projectStore, uiStore } = rootStore;
    const [status, setStatus] = useState("Initializing project creation...");
    const [error, setError] = useState<string | null>(null);
    const orgSlug = params.orgSlug as string;
    const hasCreatedRef = useRef(false);

    // Handle project creation
    useEffect(() => {
        if (hasCreatedRef.current) return;
        hasCreatedRef.current = true;

        const createProject = async () => {
            try {
                setStatus("Setting up environment...");

                await pyodideService.clearFilesystem();
                // Create new project in the store
                setStatus("Creating new project...");
                const result = await projectStore.createNewProject(
                    (log: string) => {
                        uiStore.updateLogs(log);
                        setStatus(log);
                    },
                    uiStore.setActiveTab.bind(uiStore),
                    search.type
                );

                if (!result || !result.projectId) {
                    throw new Error("Failed to create project");
                }

                // Create project files with Pyodide
                setStatus("Creating project files...");
                await pyodideService.createNewProject(search.type);

                // Save to IndexedDB
                setStatus("Saving project to storage...");
                await filesystemService.saveToIndexedDB();

                if (search.prompt && search.type === 'prompt') {
                    uiStore.initialAgentPrompt = search.prompt;
                    uiStore.setAgentVisible(true);
                    setStatus("Setting up AI assistant with your prompt...");
                }

                // Manually trigger a refresh of projects
                window.dispatchEvent(new CustomEvent('projectsRefreshed'));

                // Navigate to the editor page
                setStatus("Project created successfully! Redirecting to editor...");
                setTimeout(() => {
                    navigate({
                        to: "/org/$orgSlug/$projectId",
                        params: {
                            orgSlug,
                            projectId: result.projectId
                        }
                    });
                }, 500);
            } catch (err) {
                console.error("Project creation failed:", err);
                setError(err instanceof Error ? err.message : "Unknown error occurred");
                setStatus("Project creation failed.");
            }
        };

        createProject();
    }, [navigate, orgSlug, projectStore, uiStore, search.type, search.prompt]);

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <div className="bg-destructive/15 p-6 rounded-lg max-w-md">
                    <h2 className="text-xl font-bold text-destructive mb-2">Error Creating Project</h2>
                    <p className="text-muted-foreground mb-4">{error}</p>
                    <button
                        className="bg-primary text-primary-foreground px-4 py-2 rounded"
                        onClick={() => navigate({ to: "/org/$orgSlug", params: { orgSlug } })}
                    >
                        Return to Projects
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center h-screen">
            <LoadingState size="lg" message={status} />
        </div>
    );
});

export default CreateNewProjectPage;